/**
 * <ec-search-overlay>
 *
 * Predictive-search panel with 4 states (idle, loading, results, empty).
 * Triggers (data-ec-search-trigger) live elsewhere (header button, drawer foot);
 * this component owns its own form, fetcher and result rendering.
 *
 * State machine is driven by the `data-state` attribute on the host. CSS hides
 * non-active panels via [data-state="X"] [data-state-panel] selectors.
 */

import { trapFocus } from '@ec/focus-trap';
import { RecentSearches } from '@ec/recent-searches';
import { lockScroll } from '@ec/scroll-lock';
import { RecentlyViewed } from '@theme/recently-viewed-products';

const DEBOUNCE_MS = 200;
const RESOURCE_TYPES = 'product,collection,article';
const RESOURCE_LIMIT = 8;

class EcSearchOverlay extends HTMLElement {
  #input = null;
  #form = null;
  #clearBtn = null;
  #recentList = null;
  #recentEmpty = null;
  #recentClearBtn = null;
  #viewedSlot = null;
  #viewedEmpty = null;
  #resultsContainer = null;
  #statusRegion = null;

  #releaseTrap = null;
  #releaseLock = null;
  #debounceTimer = null;
  #controller = null;
  #viewedController = null;
  #onKeydown = null;
  #onClick = null;
  #previousFocus = null;
  #endpoint = '';

  connectedCallback() {
    this.#input = this.querySelector('.ec-search__input');
    this.#form = this.querySelector('.ec-search__form');
    this.#clearBtn = this.querySelector('[data-ec-search-clear]');
    this.#recentList = this.querySelector('[data-ec-recent-list]');
    this.#recentEmpty = this.querySelector('[data-ec-recent-empty]');
    this.#recentClearBtn = this.querySelector('[data-ec-recent-clear]');
    this.#viewedSlot = this.querySelector('[data-ec-viewed-slot]');
    this.#viewedEmpty = this.querySelector('[data-ec-viewed-empty]');
    this.#resultsContainer = this.querySelector('[data-ec-search-results]');
    this.#statusRegion = this.querySelector('[data-ec-search-status]');
    this.#endpoint = this.#resolveEndpoint();

    this.#onKeydown = (event) => {
      if (event.key === 'Escape' && !this.hidden) {
        event.preventDefault();
        this.close();
      }
    };

    this.#onClick = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      // Close affordances live both inside the panel (back button, clear
      // button) and outside (backdrop), so this check runs document-wide.
      if (target.closest('[data-ec-search-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      // Other handlers only react to clicks within the panel itself.
      if (!this.contains(target)) return;

      const removeBtn = target.closest('[data-ec-recent-remove]');
      if (removeBtn instanceof HTMLElement) {
        event.preventDefault();
        event.stopPropagation();
        const q = removeBtn.dataset.query;
        if (q) RecentSearches.remove(q);
        this.#renderRecent();
        return;
      }

      if (target.closest('[data-ec-recent-clear]')) {
        event.preventDefault();
        RecentSearches.clear();
        this.#renderRecent();
        return;
      }

      if (target.closest('[data-ec-search-clear]')) {
        event.preventDefault();
        if (this.#input instanceof HTMLInputElement) {
          this.#input.value = '';
          this.#input.focus();
        }
        this.#cancelInflight();
        this.#setState('idle');
        this.#updateClearButton();
      }
    };

    document.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('click', this.#onClick);
    this.#input?.addEventListener('input', this.#onInput);
    this.#form?.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('click', this.#onClick);
    this.#input?.removeEventListener('input', this.#onInput);
    this.#form?.removeEventListener('submit', this.#onSubmit);
    this.#cancelInflight();
    this.#cancelViewed();
    if (this.#debounceTimer !== null) clearTimeout(this.#debounceTimer);
    this.#releaseTrap?.();
    this.#releaseTrap = null;
    this.#releaseLock?.();
    this.#releaseLock = null;
  }

  open(opts = {}) {
    if (!this.hidden) return;
    this.#previousFocus =
      opts.previousFocus instanceof HTMLElement
        ? opts.previousFocus
        : document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
    // Only touch aria-expanded on elements that already advertise it (the
    // header trigger). Other previously-focused elements shouldn't be polluted.
    if (this.#previousFocus?.hasAttribute('aria-expanded')) {
      this.#previousFocus.setAttribute('aria-expanded', 'true');
    }
    this.hidden = false;
    this.#syncBackdropOffset();
    document.body.dataset.ecSearchOpen = 'true';
    this.#releaseLock = lockScroll();
    this.#setState('idle');
    this.#renderRecent();
    this.#hydrateViewed();
    this.dispatchEvent(new CustomEvent('ec:search:open', { bubbles: true }));
    this.#releaseTrap = trapFocus(this);
    requestAnimationFrame(() => {
      if (this.#input instanceof HTMLInputElement) this.#input.focus();
    });
  }

  close() {
    if (this.hidden) return;
    this.hidden = true;
    delete document.body.dataset.ecSearchOpen;
    this.#cancelInflight();
    this.#cancelViewed();
    if (this.#input instanceof HTMLInputElement) this.#input.value = '';
    this.#setState('idle');
    this.#updateClearButton();
    this.#releaseTrap?.();
    this.#releaseTrap = null;
    this.#releaseLock?.();
    this.#releaseLock = null;
    if (this.#previousFocus) {
      if (this.#previousFocus.hasAttribute('aria-expanded')) {
        this.#previousFocus.setAttribute('aria-expanded', 'false');
      }
      if (document.body.contains(this.#previousFocus)) {
        this.#previousFocus.focus({ preventScroll: true });
      }
    }
    this.#previousFocus = null;
    this.dispatchEvent(new CustomEvent('ec:search:close', { bubbles: true }));
  }

  #setState(state) {
    this.dataset.state = state;
  }

  // Announce state transitions in an always-visible live region. The results
  // container is hidden by CSS when not active, so aria-live there is silent.
  #announce(message) {
    if (!(this.#statusRegion instanceof HTMLElement)) return;
    this.#statusRegion.textContent = message;
  }

  // Set the backdrop top to the header's bottom edge so the announcement
  // bar + header stay outside the dimmed area. Without this the backdrop
  // would cover the whole viewport since `inset: 0` is the default.
  #syncBackdropOffset() {
    const header = this.closest('ec-header');
    if (!(header instanceof HTMLElement)) return;
    const bottom = Math.max(0, header.getBoundingClientRect().bottom);
    document.body.style.setProperty('--ec-search-backdrop-top', `${bottom}px`);
  }

  #onInput = () => {
    this.#updateClearButton();
    if (this.#debounceTimer !== null) clearTimeout(this.#debounceTimer);

    const value = this.#input instanceof HTMLInputElement ? this.#input.value.trim() : '';
    const minChars = Math.max(1, Number(this.dataset.minChars) || 2);

    if (value.length < minChars) {
      this.#cancelInflight();
      this.#setState('idle');
      return;
    }

    this.#debounceTimer = window.setTimeout(() => {
      this.#debounceTimer = null;
      this.#fetchResults(value);
    }, DEBOUNCE_MS);
  };

  #onSubmit = (event) => {
    const value = this.#input instanceof HTMLInputElement ? this.#input.value.trim() : '';
    if (!value) {
      event.preventDefault();
      return;
    }
    RecentSearches.add(value);
  };

  #updateClearButton() {
    if (!this.#clearBtn || !(this.#input instanceof HTMLInputElement)) return;
    this.#clearBtn.hidden = this.#input.value.length === 0;
  }

  #renderRecent() {
    if (!this.#recentList) return;
    const queries = RecentSearches.get();
    this.#recentList.replaceChildren();

    if (queries.length === 0) {
      if (this.#recentEmpty instanceof HTMLElement) this.#recentEmpty.hidden = false;
      if (this.#recentClearBtn instanceof HTMLElement) this.#recentClearBtn.hidden = true;
      return;
    }

    if (this.#recentEmpty instanceof HTMLElement) this.#recentEmpty.hidden = true;
    if (this.#recentClearBtn instanceof HTMLElement) this.#recentClearBtn.hidden = false;

    const searchUrl = window.Theme?.routes?.search_url || '/search';
    const removeLabel = this.dataset.i18nRecentRemove || 'Remove';

    for (const q of queries) {
      const li = document.createElement('li');
      li.className = 'ec-search__recent-item';

      const link = document.createElement('a');
      link.className = 'ec-search__recent-link';
      link.href = `${searchUrl}?q=${encodeURIComponent(q)}`;

      const icon = document.createElement('span');
      icon.className = 'ec-search__recent-icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>';

      const text = document.createElement('span');
      text.className = 'ec-search__recent-text';
      text.textContent = q;

      link.append(icon, text);

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'ec-search__recent-remove';
      removeBtn.dataset.ecRecentRemove = '';
      removeBtn.dataset.query = q;
      removeBtn.setAttribute('aria-label', removeLabel);
      removeBtn.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>';

      li.append(link, removeBtn);
      this.#recentList.append(li);
    }
  }

  async #hydrateViewed() {
    if (!this.#viewedSlot) return;

    const ids = RecentlyViewed.getProducts();
    if (!ids.length) {
      this.#viewedSlot.replaceChildren();
      if (this.#viewedEmpty instanceof HTMLElement) this.#viewedEmpty.hidden = false;
      return;
    }

    if (this.#viewedEmpty instanceof HTMLElement) this.#viewedEmpty.hidden = true;

    // Cancel any prior hydration so quick open/close cycles don't overwrite
    // the slot in the wrong order.
    this.#cancelViewed();
    this.#viewedController = new AbortController();

    try {
      const q = ids.map((id) => `id:${id}`).join(' OR ');
      const url = new URL(window.Theme?.routes?.search_url || '/search', location.origin);
      url.searchParams.set('q', q);
      url.searchParams.set('sections', 'ec-search-recent');

      const res = await fetch(url.toString(), {
        signal: this.#viewedController.signal,
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const html = data['ec-search-recent'];

      if (typeof html === 'string' && html.trim() !== '') {
        // Section Rendering API wraps output in the section's outer markup;
        // strip the wrapper to get inner content only.
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        const inner = tmp.firstElementChild?.innerHTML ?? tmp.innerHTML;
        this.#viewedSlot.innerHTML = inner;
      } else if (this.#viewedEmpty instanceof HTMLElement) {
        this.#viewedEmpty.hidden = false;
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
      if (this.#viewedEmpty instanceof HTMLElement) this.#viewedEmpty.hidden = false;
    }
  }

  #cancelViewed() {
    if (this.#viewedController) {
      this.#viewedController.abort();
      this.#viewedController = null;
    }
  }

  async #fetchResults(query) {
    if (!this.#resultsContainer) return;
    this.#cancelInflight();
    this.#controller = new AbortController();
    this.#setState('loading');
    this.#announce(this.dataset.i18nLoading || 'Loading…');

    const url =
      this.#endpoint +
      (this.#endpoint.includes('?') ? '&' : '?') +
      new URLSearchParams({
        q: query,
        'resources[type]': RESOURCE_TYPES,
        'resources[options][unavailable_products]': 'last',
        'resources[limit]': String(RESOURCE_LIMIT),
        'resources[limit_scope]': 'each',
      }).toString();

    try {
      const res = await fetch(url, {
        signal: this.#controller.signal,
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      this.#renderResults(data?.resources?.results || {}, query);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      this.#renderError();
    }
  }

  #cancelInflight() {
    if (this.#controller) {
      this.#controller.abort();
      this.#controller = null;
    }
  }

  #renderResults(results, query) {
    if (!this.#resultsContainer) return;
    this.#resultsContainer.replaceChildren();

    const products = Array.isArray(results.products) ? results.products : [];
    const collections = Array.isArray(results.collections) ? results.collections : [];
    const articles = Array.isArray(results.articles) ? results.articles : [];
    const total = products.length + collections.length + articles.length;

    if (total === 0) {
      this.#setState('empty');
      this.#announce(this.dataset.i18nNoResultsTitle || 'No results');
      return;
    }
    this.#setState('results');
    this.#announce(this.#tSeeAll(total));

    const grid = document.createElement('div');
    grid.className = 'ec-search__results-grid';

    const sidebar = document.createElement('aside');
    sidebar.className = 'ec-search__results-sidebar';

    if (collections.length > 0) {
      sidebar.append(
        this.#buildSidebarList(
          this.dataset.i18nCategoriesTitle || 'Categories',
          collections,
          'collection',
          query
        )
      );
    }
    if (articles.length > 0) {
      sidebar.append(
        this.#buildSidebarList(
          this.dataset.i18nArticlesTitle || 'Articles',
          articles,
          'article',
          query
        )
      );
    }
    if (sidebar.children.length > 0) grid.append(sidebar);

    const main = document.createElement('div');
    main.className = 'ec-search__results-main';

    if (products.length > 0) {
      const heading = document.createElement('h3');
      heading.className = 'ec-search__col-title';
      heading.textContent = this.dataset.i18nProductsTitle || 'Products';
      main.append(heading);

      const prodGrid = document.createElement('ul');
      prodGrid.className = 'ec-search__products-grid';
      prodGrid.setAttribute('role', 'list');
      for (const p of products) {
        prodGrid.append(this.#buildProductCard(p, query));
      }
      main.append(prodGrid);
    }

    grid.append(main);
    this.#resultsContainer.append(grid);

    // Bottom CTA "See all (N)"
    const footer = document.createElement('div');
    footer.className = 'ec-search__see-all-row';

    const link = document.createElement('a');
    link.className = 'ec-search__see-all-cta';
    link.href = `${window.Theme?.routes?.search_url || '/search'}?q=${encodeURIComponent(query)}`;
    link.textContent = this.#tSeeAll(total);
    footer.append(link);

    this.#resultsContainer.append(footer);

    this.dispatchEvent(
      new CustomEvent('ec:search:results', { bubbles: true, detail: { count: total, query } })
    );
  }

  #buildSidebarList(title, items, kind, query) {
    const section = document.createElement('section');
    section.className = `ec-search__sidebar-block ec-search__sidebar-block--${kind}`;

    const heading = document.createElement('h3');
    heading.className = 'ec-search__col-title';
    heading.textContent = title;
    section.append(heading);

    const list = document.createElement('ul');
    list.className = 'ec-search__sidebar-list';
    list.setAttribute('role', 'list');

    for (const item of items) {
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.className = 'ec-search__sidebar-link';
      a.href = item.url || '#';
      a.innerHTML = highlightText(item.title || '', query);
      li.append(a);
      list.append(li);
    }
    section.append(list);
    return section;
  }

  #buildProductCard(item, query) {
    const li = document.createElement('li');
    li.className = 'ec-search__product-item';

    const a = document.createElement('a');
    a.className = 'ec-search-product';
    a.href = item.url || '#';

    const media = document.createElement('span');
    media.className = 'ec-search-product__media';
    if (item.image) {
      const img = document.createElement('img');
      img.className = 'ec-search-product__image';
      img.src = appendImageWidth(item.image, 240);
      img.srcset = `${appendImageWidth(item.image, 240)} 1x, ${appendImageWidth(item.image, 480)} 2x`;
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      media.append(img);
    }

    const body = document.createElement('span');
    body.className = 'ec-search-product__body';

    if (item.vendor || item.type) {
      const cat = document.createElement('span');
      cat.className = 'ec-search-product__cat';
      cat.textContent = item.type || item.vendor || '';
      body.append(cat);
    }

    const title = document.createElement('span');
    title.className = 'ec-search-product__title';
    title.innerHTML = highlightText(item.title || '', query);
    body.append(title);

    if (typeof item.price === 'string' && item.price) {
      const price = document.createElement('span');
      price.className = 'ec-search-product__price';
      if (item.compare_at_price_max && stripPriceTags(item.compare_at_price_max)) {
        const compareNumeric = parsePrice(item.compare_at_price_max);
        const priceNumeric = parsePrice(item.price);
        if (compareNumeric > priceNumeric) {
          const old = document.createElement('s');
          old.className = 'ec-search-product__price-old';
          old.textContent = stripPriceTags(item.compare_at_price_max);
          price.append(old, ' ');
        }
      }
      price.append(document.createTextNode(stripPriceTags(item.price)));
      body.append(price);
    }

    a.append(media, body);
    li.append(a);
    return li;
  }

  #tSeeAll(count) {
    if (count === 1 && this.dataset.i18nSeeAllOne) return this.dataset.i18nSeeAllOne;
    const tmpl = this.dataset.i18nSeeAllOther || 'See all ##COUNT## results';
    return tmpl.replace('##COUNT##', String(count));
  }

  #renderError() {
    if (!this.#resultsContainer) return;
    this.#resultsContainer.replaceChildren();
    const p = document.createElement('p');
    p.className = 'ec-search__error';
    p.textContent = this.dataset.i18nError || 'Connection error.';
    this.#resultsContainer.append(p);
    this.#setState('results');
    this.#announce(p.textContent);
  }

  #resolveEndpoint() {
    const base = window.Theme?.routes?.predictive_search_url || '/search/suggest';
    return base.endsWith('.json') ? base : `${base}.json`;
  }
}

function appendImageWidth(url, width) {
  if (!url || typeof url !== 'string') return url;
  try {
    const u = new URL(url, window.location.origin);
    u.searchParams.set('width', String(width));
    return u.toString();
  } catch {
    return url;
  }
}

function stripPriceTags(html) {
  const el = document.createElement('span');
  el.innerHTML = html;
  return el.textContent?.trim() || '';
}

function parsePrice(html) {
  const txt = stripPriceTags(html);
  const num = parseFloat(txt.replace(/[^\d.,]/g, '').replace(',', '.'));
  return Number.isFinite(num) ? num : 0;
}

function highlightText(text, query) {
  if (!text) return '';
  const escaped = escapeHtml(text);
  if (!query) return escaped;
  const nq = normalize(query.trim());
  if (!nq) return escaped;
  const nt = normalize(text);
  const idx = nt.indexOf(nq);
  if (idx === -1) return escaped;
  // Reconstruct using original text but with the matched slice wrapped in <mark>.
  const before = escapeHtml(text.slice(0, idx));
  const match = escapeHtml(text.slice(idx, idx + nq.length));
  const after = escapeHtml(text.slice(idx + nq.length));
  return `${before}<mark>${match}</mark>${after}`;
}

function normalize(s) {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

if (!customElements.get('ec-search-overlay')) {
  customElements.define('ec-search-overlay', EcSearchOverlay);
}
